import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  KLACHT_TYPES,
  KLACHT_REDENEN,
  KLANT_VERWACHT_OPTIES,
} from '../../lib/klacht-constants';
import { KlachtError, registerKlacht } from '../../lib/klachten';
import { useKlachtDialogStore } from '../../stores/klacht-dialog-store';

/**
 * Dialog op order-detail om een klacht te registreren. Lees uit
 * `useKlachtDialogStore` of we open zijn (`openDialog === 'register'`) en
 * vergt `payload.werkOrderId` om een klacht aan een WO te koppelen.
 *
 * Na success: store sluit, navigatie naar `/klachten/{id}` zodat office
 * meteen door kan gaan met action-toevoegen.
 */
export function KlachtRegisterDialog() {
  const openDialog = useKlachtDialogStore((s) => s.openDialog);
  const payload = useKlachtDialogStore((s) => s.payload);
  const close = useKlachtDialogStore((s) => s.close);
  const navigate = useNavigate();

  const isOpen = openDialog === 'register';
  const werkOrderId = payload.werkOrderId;
  const werkOrderNummer = payload.werkOrderNummer;

  const [type, setType] = useState<string>(KLACHT_TYPES[0]?.code ?? '');
  const [redenen, setRedenen] = useState<string[]>([]);
  const [klantVerwacht, setKlantVerwacht] = useState<string>('');
  const [beschrijving, setBeschrijving] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset velden bij openen
  useEffect(() => {
    if (isOpen) {
      setType(KLACHT_TYPES[0]?.code ?? '');
      setRedenen([]);
      setKlantVerwacht('');
      setBeschrijving('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const toggleReden = useCallback((reden: string) => {
    setRedenen((prev) =>
      prev.includes(reden) ? prev.filter((r) => r !== reden) : [...prev, reden]
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!werkOrderId) {
      setError('Geen werkorder-id meegegeven');
      return;
    }
    if (!beschrijving.trim()) {
      setError('Beschrijving is verplicht');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await registerKlacht({
        originalWerkOrderId: werkOrderId,
        type,
        redenen: redenen.length > 0 ? redenen : undefined,
        klantVerwacht: klantVerwacht || undefined,
        beschrijving: beschrijving.trim(),
      });
      close();
      // Bestaande open klacht op de order: de melding staat daar als update,
      // er is geen tweede klacht. De klachtpagina toont het meteen.
      navigate(`/klachten/${result.klachtId}`, result.bestaand ? { state: { meldingToegevoegd: result.klachtNummer } } : undefined);
    } catch (err) {
      const msg = err instanceof KlachtError ? err.message : 'Klacht registreren mislukt';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [werkOrderId, beschrijving, type, redenen, klantVerwacht, close, navigate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Klacht registreren</h2>
          <p className="text-xs text-slate-500 mt-1">
            {werkOrderNummer ? `Werkorder ${werkOrderNummer}` : 'Werkorder onbekend'}
          </p>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          <label className="block">
            <span className="text-sm text-slate-700">Type klacht</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              disabled={submitting}
            >
              {KLACHT_TYPES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <fieldset>
            <legend className="text-sm text-slate-700 mb-1">Oorzaken (optioneel)</legend>
            <div className="grid grid-cols-2 gap-1.5">
              {KLACHT_REDENEN.map((reden) => (
                <label
                  key={reden}
                  className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={redenen.includes(reden)}
                    onChange={() => toggleReden(reden)}
                    disabled={submitting}
                    className="rounded border-slate-300"
                  />
                  {reden}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="block">
            <span className="text-sm text-slate-700">Wat verwacht de klant?</span>
            <select
              value={klantVerwacht}
              onChange={(e) => setKlantVerwacht(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              disabled={submitting}
            >
              <option value="">— Onbekend —</option>
              {KLANT_VERWACHT_OPTIES.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm text-slate-700">
              Beschrijving <span className="text-red-600">*</span>
            </span>
            <textarea
              value={beschrijving}
              onChange={(e) => setBeschrijving(e.target.value)}
              rows={4}
              placeholder="Wat is er aan de hand? Welke kleden, welke vlekken/schade?"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
          </label>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !beschrijving.trim()}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || !beschrijving.trim()
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            )}
          >
            {submitting ? 'Registreren…' : 'Klacht registreren'}
          </button>
        </div>
      </div>
    </div>
  );
}
